import { useEffect, useRef, useState } from "react";

import { useStore } from "hooks/use-store";

export const useAnimatedValue = (
  target: number | null | undefined,
  fps = 30,
  speed = 0.2
) => {
  const [value, setValue] = useState<number | undefined>(target ?? undefined);
  const browserSpeed = useStore((state) => state.browserSpeed);
  const isBrowserSlow = browserSpeed && browserSpeed < 300;

  // Keep the latest target in a ref so the effect doesn't have to re-subscribe
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    // On slow browsers: just snap to the target and don't animate
    if (isBrowserSlow) {
      setValue(targetRef.current ?? undefined);
      return;
    }

    let frameId: number;
    const frameDuration = 1000 / fps;
    let lastTime = performance.now();

    const tick = (now: number) => {
      // Throttle to desired FPS
      if (now - lastTime >= frameDuration) {
        lastTime = now;

        setValue((prev) => {
          const target = targetRef.current;
          if (target === undefined || target === null) return undefined;
          if (prev === undefined) return target;
          const next = prev + (target - prev) * speed;
          return next;
        });
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [fps, speed, isBrowserSlow]);

  return isBrowserSlow ? target ?? 0 : value ?? 0;
};
